import { useEffect, useRef } from "react";

import { highnote } from "@highnote/server/src/sdk";

import { Ad } from "./AdModel";

export type AdSurface = "carousel" | "space_pinned";

export type AdTrackerParams = {
  ad: Ad;
  spaceId?: string | null;
  surface: AdSurface;
  /**
   * Optional override for the tracker call. Tests inject a mock here so
   * they don't need to mock the full `@highnote/server/src/sdk` module.
   */
  track?: (props: {
    adId: string;
    eventType: "impression" | "click";
    campaignId?: string;
    advertiserName?: string;
    adType?: Ad["adType"];
    spaceId?: string;
    surface?: string;
  }) => Promise<unknown>;
};

/**
 * Default fraction of the ad that must be visible before we count an
 * impression. 0.5 matches the Flutter repository + industry MRC
 * guidance for "viewable" impressions on mobile/web content.
 */
const DEFAULT_IMPRESSION_THRESHOLD = 0.5;

const buildEventProps = (
  ad: Ad,
  spaceId: string | null | undefined,
  surface: AdSurface,
  eventType: "impression" | "click",
) => ({
  adId: ad.id,
  eventType,
  campaignId: ad.campaignId ?? undefined,
  advertiserName: ad.advertiserName || undefined,
  adType: ad.adType,
  spaceId: spaceId ?? undefined,
  surface,
});

/**
 * Fires a single `impression` event the first time the attached element
 * crosses the visibility threshold. Subsequent visibility changes are
 * ignored — the impression is recorded at most once per mount.
 *
 * Returns a ref to attach to the DOM node whose visibility should be
 * observed. When `IntersectionObserver` is not available (e.g. in older
 * jsdom versions) the hook falls back to firing the impression on mount
 * so we never under-count in test environments or legacy browsers.
 */
export const useAdImpressionTracker = ({
  ad,
  spaceId,
  surface,
  track,
}: AdTrackerParams) => {
  const nodeRef = useRef<HTMLElement | null>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    firedRef.current = false;
  }, [ad.id]);

  useEffect(() => {
    const node = nodeRef.current;
    const fire = () => {
      if (firedRef.current) return;
      firedRef.current = true;
      const caller = track ?? highnote.trackAdEvent;
      // Fire-and-forget: the SDK wrapper already swallows errors, but
      // an extra `.catch` here guards against a caller-supplied mock
      // that forgets to do so. No await — analytics must never block
      // paint.
      Promise.resolve(
        caller(buildEventProps(ad, spaceId, surface, "impression")),
      ).catch((e) => {
        console.warn("[ads] impression track failed:", e);
      });
    };

    if (!node) {
      // No node to observe yet; fall back to firing on mount so we
      // don't miss impressions entirely (e.g. in jsdom or when a
      // consumer renders without a ref).
      fire();
      return;
    }

    if (typeof IntersectionObserver === "undefined") {
      // Legacy browsers: approximate a visible impression as "mounted".
      fire();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            fire();
            observer.disconnect();
            break;
          }
        }
      },
      { threshold: DEFAULT_IMPRESSION_THRESHOLD },
    );
    observer.observe(node);
    return () => observer.disconnect();
    // Intentionally depends on ad id + surface so re-mounts with a new
    // ad start fresh, and re-mounts of the same ad on the same surface
    // do not double-count.
  }, [ad.id, surface, spaceId, track]);

  return nodeRef;
};

/**
 * Fires a single `click` event for an ad. Safe to call from an onClick
 * handler — always resolves, never throws, never blocks.
 */
export const trackAdClick = (
  ad: Ad,
  spaceId: string | null | undefined,
  surface: AdSurface,
  track?: AdTrackerParams["track"],
): void => {
  const caller = track ?? highnote.trackAdEvent;
  Promise.resolve(caller(buildEventProps(ad, spaceId, surface, "click"))).catch(
    (e) => {
      console.warn("[ads] click track failed:", e);
    },
  );
};
